/**
 * VaultShareLanding.jsx — Iteration 45. Public, no-auth landing page at
 * /vault/share/:token that recipients see when they click a founder's share
 * link. Founderboard-branded, startup details on the side, 4-digit passcode
 * gate, then download list. Every interaction is logged server-side.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Vault, Lock, ShieldCheck, ClockCounterClockwise, FileText, DownloadSimple, ArrowRight, Warning, Spinner } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCENT = '#10b981';

const fmtBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtExpiry = (iso) => {
  if (!iso) return '—';
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3600000);
  if (h < 24) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
};

const VaultShareLanding = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [passcode, setPasscode] = useState('');
  const [busy, setBusy] = useState(false);
  const [jwt, setJwt] = useState(null);
  const [files, setFiles] = useState(null);
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/vault/share/${token}/info`);
        if (!cancelled) setInfo(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Link unavailable');
      } finally { if (!cancelled) setLoadingInfo(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const verify = async (e) => {
    e?.preventDefault();
    if (!/^[0-9]{4}$/.test(passcode)) { toast.error('Enter 4 digits'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/vault/share/${token}/verify`, { passcode });
      setJwt(r.data.token);
      const f = await api.get(`/vault/share/${token}/files`, { headers: { 'X-Vault-Token': r.data.token } });
      setFiles(f.data.files || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Wrong passcode'); }
    finally { setBusy(false); }
  };

  const download = async (f) => {
    setDownloading(f.id);
    try {
      const r = await api.get(`/vault/share/${token}/files/${f.id}/download`, {
        headers: { 'X-Vault-Token': jwt },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = f.filename || 'file';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
    finally { setDownloading(''); }
  };

  if (loadingInfo) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-text-muted text-sm inline-flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Verifying link…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="vault-share-error">
        <div className="bg-surface border border-border rounded-2xl p-8 max-w-md w-full text-center">
          <Warning size={28} className="mx-auto mb-3 text-warning" />
          <h2 className="text-xl font-heading font-bold mb-2">Link unavailable</h2>
          <p className="text-sm text-text-secondary">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-text-primary" data-testid="vault-share-landing">
      {/* Header */}
      <div className="border-b border-border bg-surface">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}>
              <Vault size={16} weight="fill" color="white" />
            </span>
            <div className="leading-none">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: ACCENT }}>Founderboard</div>
              <div className="text-sm font-heading font-bold">Vault</div>
            </div>
          </div>
          <div className="text-[10px] text-text-muted inline-flex items-center gap-1">
            <ClockCounterClockwise size={9} weight="bold" /> {fmtExpiry(info.expires_at)}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Side panel — startup details */}
        <aside className="bg-surface border border-border rounded-2xl p-5 h-fit" data-testid="vault-share-side">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2">Shared by</div>
          <div className="flex items-center gap-3 mb-4">
            {info.startup?.logo_url ? (
              <img src={`${API}/files/${info.startup.logo_url}/public`} className="w-10 h-10 rounded object-cover" alt="" />
            ) : (
              <div className="w-10 h-10 rounded bg-background border border-border inline-flex items-center justify-center font-mono font-bold" style={{ color: ACCENT }}>
                {(info.startup?.name || 'ST').slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-heading font-bold text-text-primary truncate">{info.startup?.name || 'Startup'}</div>
              <div className="text-[11px] text-text-muted truncate">{info.startup?.sector || '—'}</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">Dataroom</div>
          <div className="text-sm font-medium mb-1">{info.dataroom?.name}</div>
          <p className="text-[11px] text-text-muted">{info.dataroom?.blurb}</p>
          <div className="border-t border-border mt-4 pt-3 text-[11px] text-text-muted leading-relaxed">
            <ShieldCheck size={11} weight="fill" className="inline mr-1" style={{ color: ACCENT }} />
            Your view + downloads are logged for the founder&rsquo;s security.
          </div>
        </aside>

        {/* Main column — passcode gate OR file list */}
        <main className="bg-surface border border-border rounded-2xl p-6 md:p-8">
          {!files ? (
            <div className="max-w-sm mx-auto">
              <div className="text-center mb-6">
                <span className="w-12 h-12 rounded-xl inline-flex items-center justify-center mb-3" style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}>
                  <Lock size={22} weight="fill" />
                </span>
                <h1 className="text-2xl font-heading font-bold leading-[1.1]" style={{ letterSpacing: '-0.02em' }}>Enter passcode</h1>
                <p className="text-sm text-text-secondary mt-1.5">The founder will have shared a 4-digit code with you separately. Enter it below to unlock <strong className="text-text-primary">{info.dataroom?.name}</strong>.</p>
              </div>
              <form onSubmit={verify} data-testid="vault-share-form">
                <input value={passcode} maxLength={4} inputMode="numeric" autoFocus
                  onChange={(e) => setPasscode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                  placeholder="••••" data-testid="vault-share-passcode"
                  className="w-full bg-background border border-border rounded-lg px-4 py-4 text-3xl font-mono tracking-[0.5em] text-center mb-4 focus:outline-none focus:border-text-primary" />
                <button type="submit" disabled={busy || passcode.length !== 4} data-testid="vault-share-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                  {busy ? <Spinner size={14} className="animate-spin" /> : <Lock size={14} weight="bold" />} Unlock
                  {!busy && <ArrowRight size={14} weight="bold" />}
                </button>
              </form>
            </div>
          ) : (
            <div data-testid="vault-share-files">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Files</div>
                  <h2 className="text-xl font-heading font-bold">{info.dataroom?.name}</h2>
                </div>
                <div className="text-[11px] text-text-muted inline-flex items-center gap-1"><ShieldCheck size={11} weight="fill" style={{ color: ACCENT }} /> Verified · {files.length} file{files.length === 1 ? '' : 's'}</div>
              </div>
              {files.length === 0 ? (
                <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted">No files have been uploaded to this dataroom yet.</div>
              ) : (
                <div className="space-y-2">
                  {files.map((f) => (
                    <div key={f.id} className="bg-background border border-border rounded-lg p-3 flex items-center gap-3" data-testid={`vault-share-file-${f.id}`}>
                      <FileText size={16} weight="bold" className="text-text-muted flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">{f.filename}</div>
                        <div className="text-[10px] text-text-muted">{fmtBytes(f.size)} · uploaded {new Date(f.created_at).toLocaleDateString('en-IN')}</div>
                      </div>
                      <button onClick={() => download(f)} disabled={downloading === f.id} data-testid={`vault-share-download-${f.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                        {downloading === f.id ? <Spinner size={11} className="animate-spin" /> : <DownloadSimple size={11} weight="bold" />} Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default VaultShareLanding;
